import type { Priority, Task } from "@app/core";
import { childrenOf, subtaskProgress } from "@app/core";
import { useEffect, useRef, useState } from "react";
import type { I18nKey } from "../../../lib/language.js";
import { useI18n } from "../../../lib/useI18n.js";
import { useTasksStore } from "../../../stores/index.js";
import { Check } from "./bits.js";
import { SUB_PRI_FLAG } from "./palette.js";

const SUB_PRIORITIES: Priority[] = ["none", "low", "medium", "high"];
const PRI_NAME: Record<Priority, I18nKey> = {
  none: "taskForm.noPriority",
  low: "sidebar.priority.low",
  medium: "sidebar.priority.medium",
  high: "sidebar.priority.high",
};

interface Props {
  /** The task being edited, or null when creating a new one. */
  editing: Task | null;
  /** Buffered subtask titles for the not-yet-saved new task (create mode). */
  pending: string[];
  onPendingChange: (next: string[]) => void;
}

function FlagRow({ value, onPick }: { value: Priority; onPick: (p: Priority) => void }) {
  const { t } = useI18n();
  return (
    <div className="sub-flags" role="group" aria-label={t("sidebar.priority")}>
      {SUB_PRIORITIES.map((p) => (
        <button
          key={p}
          type="button"
          className={"sub-flag" + (value === p ? " is-sel" : "")}
          data-pri={p}
          style={{ ["--fc" as string]: SUB_PRI_FLAG[p] }}
          onClick={() => onPick(p)}
          title={t(PRI_NAME[p])}
          aria-label={t(PRI_NAME[p])}
          aria-pressed={value === p}
        >
          <svg className="mini-flag" viewBox="0 0 16 16" aria-hidden="true">
            <path className="pole" d="M3.4 1.5 V14.5" />
            <path className="pennant" d="M3.4 2 H13 L10.4 5.25 L13 8.5 H3.4 Z" />
          </svg>
        </button>
      ))}
    </div>
  );
}

/** Title input that buffers edits locally and commits on blur / Enter. */
function TitleInput({ title, autoFocus, onCommit, onEmptyBackspace }: {
  title: string;
  autoFocus?: boolean;
  onCommit: (t: string) => void;
  onEmptyBackspace?: () => void;
}) {
  const { t } = useI18n();
  const [draft, setDraft] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => { if (autoFocus) ref.current?.focus(); }, [autoFocus]);
  const value = draft ?? title;
  const commit = () => { if (draft !== null && draft !== title) onCommit(draft); setDraft(null); };
  return (
    <input
      ref={ref}
      className="subtask-input"
      value={value}
      placeholder={t("taskForm.subtask")}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); commit(); }
        if (e.key === "Backspace" && value === "") { e.preventDefault(); onEmptyBackspace?.(); }
      }}
    />
  );
}

/** Subtasks editor. In edit mode it reads/writes real child tasks via the
 *  store (done state, title, per-subtask priority); in create mode it buffers
 *  titles, which the submit handler turns into real subtasks once the parent
 *  is saved. New subtasks are created undated. */
export function SubtasksPanel({ editing, pending, onPendingChange }: Props) {
  const { t } = useI18n();
  const tasks = useTasksStore((s) => s.tasks);
  const add = useTasksStore((s) => s.add);
  const edit = useTasksStore((s) => s.edit);
  const complete = useTasksStore((s) => s.complete);
  const remove = useTasksStore((s) => s.remove);
  const [focusLast, setFocusLast] = useState(false);

  const parentId = editing ? (editing.virtual && editing.parent_id ? editing.parent_id : editing.id) : null;
  const children = parentId ? childrenOf(tasks, parentId) : [];
  const progress = parentId ? subtaskProgress(tasks, parentId) : { done: 0, total: pending.length };

  async function addRow() {
    setFocusLast(true);
    if (parentId) {
      await add({ title: "", subtask_parent_id: parentId, due_at: null, is_event: false, calendar_id: editing!.calendar_id, subtask_order: children.length });
    } else {
      onPendingChange([...pending, ""]);
    }
  }

  return (
    <div className="subtasks">
      <div className="subtasks-head">
        <span className="field-label">{t("taskForm.subtasks")}</span>
        {progress.total > 0 && (
          <span className="subtasks-count">{progress.done}/{progress.total}</span>
        )}
      </div>

      {(parentId ? children.length : pending.length) > 0 && (
        <div className="subtask-list">
          {parentId
            ? children.map((c, i) => (
                <div key={c.id} className={"subtask-row" + (c.status === "done" ? " is-done" : "")}>
                  <Check checked={c.status === "done"} onChange={(v) => void complete(c.id, v)} />
                  <TitleInput
                    title={c.title}
                    autoFocus={focusLast && i === children.length - 1 && c.title === ""}
                    onCommit={(t) => void edit(c.id, { title: t })}
                    onEmptyBackspace={() => void remove(c.id)}
                  />
                  {c.due_at && <span className="subtask-sched">{t("taskForm.scheduled")}</span>}
                  <FlagRow value={c.priority} onPick={(p) => void edit(c.id, { priority: p })} />
                  <button type="button" className="subtask-del" onClick={() => void remove(c.id)} aria-label={t("taskForm.removeSubtask")}>×</button>
                </div>
              ))
            : pending.map((title, i) => (
                <div key={i} className="subtask-row">
                  <input
                    className="subtask-input"
                    value={title}
                    placeholder={t("taskForm.subtask")}
                    autoFocus={focusLast && i === pending.length - 1 && title === ""}
                    onChange={(e) => onPendingChange(pending.map((x, j) => (j === i ? e.target.value : x)))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); void addRow(); }
                      if (e.key === "Backspace" && title === "") {
                        e.preventDefault();
                        onPendingChange(pending.filter((_, j) => j !== i));
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="subtask-del"
                    onClick={() => onPendingChange(pending.filter((_, j) => j !== i))}
                    aria-label={t("taskForm.removeSubtask")}
                  >×</button>
                </div>
              ))}
        </div>
      )}

      <button type="button" className="subtask-add" onClick={() => void addRow()}>
        <span className="plus" aria-hidden="true">+</span> {t("taskForm.addSubtask")}
      </button>
    </div>
  );
}
